import { FieldValues, useFormContext } from "react-hook-form";
import { cva, type VariantProps } from "class-variance-authority";

import { Content } from "./Content";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { StepProps, StepType } from "./types";

import { cn } from "@/lib/utils";

const stepStyles = {
  container: cva("flex flex-col", {
    variants: {
      size: {
        desktop: "gap-[42px]",
        dialog: "gap-4 justify-between h-full",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  contentContainer: cva("flex flex-col", {
    variants: {
      size: {
        desktop: "gap-[42px] h-full",
        dialog: "gap-[32px] flex-1 min-h-0",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
};

export type StepStyleProps = VariantProps<typeof stepStyles.container>;

export const Step = <T extends FieldValues>({
  step,
  stepNumber,
  totalSteps,
  onBack,
  onNext,
  onSkip,
  isSkippable = false,
  isNextButtonLoading,
  onItemHover,
  onStepOptionSelect,
  size = "desktop",
  isBackButtonVisible,
  isPreview = false,
  layout = "default",
  onExit,
  overlayContent,
  className,
}: StepProps<T>) => {
  const { id, title, titleTooltip, description } = step;
  const form = useFormContext();
  const formValues = form.watch() as Record<string, unknown>;
  const { isDirty, invalid } = form.getFieldState(id, form.formState);
  const isLastStep = stepNumber === totalSteps;
  const isCustomStep = step.type === StepType.CUSTOM_STEP && step.ignoreValidation;
  const isOptionalStepSkippable = step.shouldShowSkip ? step.shouldShowSkip(formValues) : true;
  const isStepSkippable = isSkippable || (Boolean(step.isOptional) && isOptionalStepSkippable);

  return (
    <div
      className={`${stepStyles.container({
        size,
      })} relative h-full phone:h-full phone:justify-between`}
    >
      <div className={cn(stepStyles.contentContainer({ size }))}>
        <Header
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title={title}
          titleTooltip={titleTooltip}
          description={description}
          size={size}
          layout={layout}
        />
        <Content
          step={step}
          onStepOptionSelect={onStepOptionSelect}
          onItemHover={onItemHover}
          size={size}
          className={className}
        />
        {isNextButtonLoading && overlayContent ? (
          <div className="absolute inset-0 z-10 flex h-full w-full items-center justify-center bg-white/70">
            {overlayContent}
          </div>
        ) : null}
      </div>
      <Footer
        isBackButtonVisible={isBackButtonVisible}
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        onExit={onExit}
        isSkippable={isStepSkippable}
        isBackDisabled={stepNumber === 1}
        isSubmit={isLastStep}
        isNextDisabled={step.isNextDisabled ?? ((!isDirty || invalid) && !isCustomStep)}
        isNextButtonLoading={isNextButtonLoading}
        size={size}
        isPreview={isPreview}
      />
    </div>
  );
};
